import { useMemo } from "react";

import { Pie, PieChart, Sector } from "@/ui-adapters/Recharts";
import { RuntimeSquareSize } from "@/ui-adapters/RuntimeSquareSize";

import { dataColors } from "./theme";

interface Props {
  size: number;
  value: number;
  referenceValue?: number;
}

export function DonutChart({ value, referenceValue, size }: React.PropsWithChildren<Props>): React.ReactNode {
  const chartData = useMemo(() => {
    const primarySlice = {
      name: "primary",
      value: value,
      fill: dataColors.primary,
    };

    if (referenceValue != null && referenceValue > value) {
      return [
        primarySlice,
        {
          name: "benchmark",
          value: referenceValue - value,
          fill: dataColors.benchmark,
        },
        {
          name: "background",
          value: 100 - referenceValue,
          fill: dataColors.pieChartGrey,
        },
      ];
    }

    return [
      primarySlice,
      {
        name: "background",
        value: 100 - value,
        fill: dataColors.pieChartGrey,
      },
    ];
  }, [value, referenceValue]);

  return (
    <RuntimeSquareSize size={size}>
      {(attributes) => (
        <figure {...attributes} className="relative my-space-sm inline-block">
          <PieChart width={size} height={size}>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={size / 2.8}
              outerRadius={size / 2}
              dataKey="value"
              startAngle={90}
              endAngle={-270}
              stroke="none"
              shape={(props) => <Sector {...props} />}
            />
          </PieChart>
          <figcaption className="absolute top-1/2 left-1/2 max-w-full -translate-1/2">
            <span className="text-heading-1 text-text-brand-secondary">{value}%</span>
          </figcaption>
        </figure>
      )}
    </RuntimeSquareSize>
  );
}
